import { message } from "antd";
import { type AxiosError } from "axios";
import { format } from "date-fns";
import { cloneDeep } from "lodash-es";
import moment from "moment";

import { isBTAdminPath } from "@btnet/services-btnet/btadmin-path";
import {
    APIError,
    BadRequestError,
    ConflictError,
    DetailedError,
    isBadRequestError,
    isLegacyFailure,
    legacyFailureHeader,
    LoginRedirectError,
    NetworkError,
    NoAccessError,
    NotFoundError,
    PreviewActionAPIError,
    ServiceUnavailableError,
    TooManyRequestsError,
    UpgradeRequiredError,
} from "@btnet/services-btnet/errors";
import type { ConflictResponse } from "@btnet/services-btnet/handlers/models/conflictResponse";

import {
    APIHandlerVersion,
    BaseResponse,
    BTSelectItem,
    type ExceptionResponse,
    type IAPIHandlerResult,
    isCanceledAPIRequestError,
} from "types/apiResponse/apiResponse";
import { CommonConstants } from "types/constants";
import { BadRequestReason, ForbiddenReason } from "types/enum";

import { type UploadProgressCallback, xhrFetch } from "utilities/api/xhrFetch";
import { getFileNameFromHeader } from "utilities/document/fileDownload.utils";
import { reportError } from "utilities/errorHelpers";
import { isNullOrUndefined, isObjectWithKey } from "utilities/object/object";
import { getPortalTypeForHeader } from "utilities/portal/portal";
import { routes } from "utilities/routes";
import { checkForNewAppVersion } from "utilities/Versioning/versionWatcher";

export const defaultErrorMessage = "An error has occurred. Please try again.";
const notLoggedInAPIResponse = "Not authorized - Please login";
export const missingEntityExceptionAPIResponse =
    "This item may have been deleted or your viewing permissions have changed.";

export const responseAs = <T>(data: unknown) => {
    return data as T;
};
/**
 * Detects when the api error is due to an unauthorized response
 * @todo We should be returning the correct status code from the api (401 Unauthorized) when the user is not authorized instead of 200
 * @param error
 */
export function isNotAuthorizedResponse(error: APIError) {
    return error.errorMessage === notLoggedInAPIResponse;
}

/**
 * Detects when the api error is due to a entity being deleted or the user no longer having view permissions
 * @todo We can remove this check when all list pages have been migrated to react, we only need to handle this when iframes are being used
 * @param error
 */
export function isMissingEntityException(error: APIError | NoAccessError) {
    const message = error instanceof APIError ? error.errorMessage : error.message;
    return message === missingEntityExceptionAPIResponse;
}

export function isConstructor<TParams extends object[], TObj>(
    func: ((...args: TParams) => TObj) | (new (...args: TParams) => TObj),
): func is new (...args: TParams) => TObj {
    return !!func.prototype && !!func.prototype.constructor.name;
}

/**
 * Displays the error message from the server, if the server does not return an error message the default will be used
 * @param customMessage by default the message returned from the api will be shown, if the api failed but has no message "An error has occurred. Please try again." is shown
 */
export function showAPIErrorMessage(e: unknown, customMessage: string = defaultErrorMessage) {
    if (e instanceof LoginRedirectError) {
        return;
    } else if (e instanceof APIError) {
        displayErrorAndLogConsoleMessage(e, customMessage);
    } else if (e instanceof BadRequestError) {
        const limitExceededError = e.errors.find(
            (err) => err.reason === BadRequestReason.LimitExceeded,
        );
        if (limitExceededError) {
            void message.error(limitExceededError.message, 5);
        } else {
            void message.error(e.message ?? defaultErrorMessage, 5);
        }
    } else if (e instanceof ConflictError || e instanceof NotFoundError) {
        return;
    } else if (e instanceof NoAccessError) {
        if (e.reason === ForbiddenReason.NotConnected) {
            handleNoJobAccessError();
        } else if (e.reason === ForbiddenReason.SubscriptionInactive) {
            handleSubscriptionInactiveError();
        } else {
            const apiErr = new APIError(e.response);
            displayErrorAndLogConsoleMessage(apiErr, customMessage);
        }
    } else {
        let messageToDisplay: string = defaultErrorMessage;
        if (customMessage !== defaultErrorMessage) {
            messageToDisplay = customMessage;
        }
        void message.error(messageToDisplay, 5);
    }
}

/**
 * Displays the error message from the server, if the server does not return an error message the default will be used
 * @param customMessage by default the message returned from the api will be shown, if the api failed but has no message "An error has occurred. Please try again." is shown
 * @deprecated Use showApixErrorMessage from apix-handler.ts instead
 */
export function showApixErrorMessage(
    e: AxiosError<ExceptionResponse>,
    customMessage: string = defaultErrorMessage,
) {
    let messageToDisplay: string = e.response?.data?.message ?? defaultErrorMessage;
    if (customMessage !== defaultErrorMessage) {
        messageToDisplay = customMessage;
    }
    void message.error(messageToDisplay, 5);
}

const displayErrorAndLogConsoleMessage = (e: APIError, customMessage: string) => {
    let messageToDisplay: string = e.errorMessage;
    if (customMessage !== defaultErrorMessage) {
        messageToDisplay = customMessage;
    }
    console.error("API Error Response", e.response);
    void message.error(messageToDisplay, 5);
};

let isRedirectingForAccessError = false;

export function handleNoJobAccessError() {
    if (isRedirectingForAccessError) return;
    isRedirectingForAccessError = true;
    window.location.assign(`/app${routes.notConnected.getLink(false)}`);
}

export function handleSubscriptionInactiveError() {
    if (isRedirectingForAccessError) return;
    isRedirectingForAccessError = true;
    window.location.assign(`/app${routes.subscriptionInactive.getLink()}`);
}

export {
    APIError,
    BadRequestError,
    ConflictError,
    DetailedError,
    isBadRequestError,
    LoginRedirectError,
    NetworkError,
    NoAccessError,
    NotFoundError,
    PreviewActionAPIError,
    ServiceUnavailableError,
    TooManyRequestsError,
    UpgradeRequiredError,
};

export interface IBlobResponse {
    blobData: Blob;
    fileName: string;
}

export const defaultPathForLoginPage = "/";

let isRedirectingToLogin = false;

/** Whether a redirect to the login page (see {@link redirectToLoginPage}) is already underway. */
export function isLoginRedirectInProgress() {
    return isRedirectingToLogin;
}

export function redirectToLoginPage(isBTAdmin: boolean) {
    const lpr = window.location.pathname + window.location.search;
    let pathForLoginPage = `${defaultPathForLoginPage}?lpr=${encodeURIComponent(
        lpr,
    )}&reason=${encodeURIComponent(CommonConstants.Login.sessionExpired)}`;

    if (isBTAdmin || isBTAdminPath()) {
        pathForLoginPage = `${routes.admin.loginLinkApix}?lpr=${encodeURIComponent(lpr)}`;
    }

    window.top!.location.assign(pathForLoginPage);

    isRedirectingToLogin = true;
}

export const getResponseAsJson = async (response: Response) => {
    let responseAsJson: BaseResponse;
    try {
        responseAsJson = await response.json();
    } catch (e) {
        // server likely returned HTML instead of JSON
        responseAsJson = new BaseResponse({});
    }

    return responseAsJson;
};

const isResponseTypeBlob = <T extends object>(
    responseType: ResponseType<T>,
): responseType is "blob" => {
    return responseType === "blob";
};

const isResponseContentTypeJson = (contentType: string | null) => {
    return contentType && contentType.lastIndexOf("application/json") !== -1;
};

const getRequestOptions = (
    method: ApiMethod,
    isPayloadJson: boolean,
    headersIn?: any,
): RequestInit => {
    const portalType = getPortalTypeForHeader();
    let headers = {
        // Omitted (rather than defaulted to PortalType.ALL) when unresolved — see
        // getPortalTypeForHeader for why a wrong-but-valid header is worse than no header.
        ...(portalType !== null ? { PortalType: portalType.toString() } : {}),
        ...headersIn,
    };

    if (isPayloadJson) {
        headers = {
            ...headers,
            "Content-Type": "application/json",
        };
    }

    return {
        method: method,
        credentials: "same-origin",
        headers,
    };
};

type ResponseType<T extends object = never> =
    | ((apiResponse: any) => T)
    | (new (apiResponse: any) => T)
    | "blob";
export const processResponse = async <T extends object>(
    response: Response,
    isBTAdmin: boolean,
    pathName: string,
    responseType: ResponseType<T>,
) => {
    const contentType = response.headers.get("content-type");

    const reqAppVersion = response.headers.get("Version");

    if (reqAppVersion) {
        checkForNewAppVersion(reqAppVersion);
    }

    // allows us to call new on function or class
    if (isResponseTypeBlob(responseType)) {
        const data = await response.blob();
        const fileName = getFileNameFromHeader(response);
        if (response.ok) {
            let errorResponseAsJson = null;
            try {
                errorResponseAsJson = await response.json();
            } catch {}
            if (isResponseContentTypeJson(contentType)) {
                await checkApiForErrors(errorResponseAsJson, isBTAdmin, pathName, contentType);
            }
            return {
                blobData: data,
                fileName,
            } as IBlobResponse;
        } else {
            const responseAsJson = await getResponseAsJson(response);
            throw new APIError(responseAsJson);
        }
    } else {
        const responseAsJson = await getResponseAsJson(response);

        await checkApiForErrors(responseAsJson, isBTAdmin, pathName, contentType);
        try {
            const data =
                responseAsJson.data !== undefined && !isApiX(pathName)
                    ? responseAsJson.data
                    : responseAsJson;
            return isConstructor(responseType) ? new responseType(data) : responseType(data);
        } catch (e) {
            // Report API entity object construction errors
            let errorMessage = `APIHandler couldn't create an instance of the given class '${responseType.name}' - maybe you're expecting a value to exist in the constructor and it does not`;
            if (isObjectWithKey(e, "stack")) {
                errorMessage += `\n${e.stack}`;
            }
            console.error(errorMessage);
            reportError(
                e,
                {
                    internalNotes: `Response: ${JSON.stringify(
                        responseAsJson,
                    )}\nError: ${errorMessage}`,
                },
                "error",
            );
            // Throw as an APIError to prevent double exception reporting in ErrorBoundary
            throw new APIError(responseAsJson);
        }
    }
};

export const checkResponseForStatusErrors = async (
    response: Response,
    url: string,
    isBTAdmin: boolean,
) => {
    // v1 failures that used to be a 200 with success=false are still handled by checkApiForErrors
    if (!response.ok && isLegacyFailure(response.headers.get(legacyFailureHeader))) {
        return response;
    }

    switch (response.status) {
        case 400:
            throw new BadRequestError(await response.json());
        case 401:
            redirectToLoginPage(isBTAdmin);
            if (isBTAdmin || isBTAdminPath()) {
                throw new LoginRedirectError(await getResponseAsJson(response), undefined, url);
            }
            break;
        case 402:
            throw new UpgradeRequiredError(await response.json());
        case 403:
            throw new NoAccessError(await getResponseAsJson(response));
        case 404:
            throw new NotFoundError(await getResponseAsJson(response));
        case 409:
            throw new ConflictError(
                (await getResponseAsJson(response)) as unknown as ConflictResponse,
            );
        case 429:
            throw new TooManyRequestsError(await getResponseAsJson(response));
        case 503: {
            const serviceUnavailableJson = await getResponseAsJson(response);
            throw new ServiceUnavailableError(
                serviceUnavailableJson,
                serviceUnavailableJson?.message,
                (
                    serviceUnavailableJson as BaseResponse & { retryAfterSeconds?: number }
                )?.retryAfterSeconds,
            );
        }
        default:
            // verify the status code is 200
            if (!response.ok) {
                if (response.status >= 400 && response.status < 500) {
                    reportError(new Error(`APIError: request returned ${response.status}`));
                }

                throw new APIError(await getResponseAsJson(response), undefined, url);
            }
    }
    return response;
};

const checkApiForErrors = async (
    responseAsJson: BaseResponse,
    isBTAdmin: boolean,
    url: string,
    contentType: string | null,
) => {
    if (!contentType) {
        return;
    }

    const isResponseContentJson = isResponseContentTypeJson(contentType);
    if (!isResponseContentJson) {
        reportError(
            new Error(
                `APIError: application/json content-type was expected, but "${contentType}" was received instead`,
            ),
        );
        throw new APIError(responseAsJson, undefined, url);
    }

    if (responseAsJson.needsToRelogin) {
        redirectToLoginPage(isBTAdmin);
        throw new LoginRedirectError(responseAsJson, undefined, url);
    }

    // verify the response has a success=true
    if (responseAsJson.success !== undefined && !responseAsJson.success) {
        throw new APIError(responseAsJson, undefined, url);
    }
};

type ApiMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface IAPIHandlerPropsBase<T> {
    method: ApiMethod;

    /* A class representing  */
    responseType: T extends IBlobResponse
        ? "blob"
        : ((apiResponse: any) => T) | (new (apiResponse: any) => T);

    /** normally JSON.stringify() will skip undefined values, setting sendUndefinedAsNull to true will change undefined values to null so the server receives them
     * @default false
     */
    sendUndefinedAsNull?: boolean;

    /* The data you want to send to the API, example: { status: 5, input: "text value" } */
    data?: any;

    isBTAdmin?: boolean;
    headers?: object;

    /**
     * Set to APIHandlerVersion.cancellable for APIHandler to return a result that is cancellable
     * @default APIHandlerVersion.none
     */
    version?: APIHandlerVersion;

    /**
     * This is used to signal not to add the request header for content-type: application/json so
     * we do not get a content type mismatch when submitting multi-part form data to an api.  The
     * primary use case currently is the file uploading.
     *  @default false
     */

    isMultiPartPostData?: boolean;

    /**
     * For controller.abort() function to ignore cancel error
     * @default true
     */
    throwExceptionOnAbort?: boolean;

    /**
     * External AbortSignal for cancelling the request.
     * When provided, the APIHandler will use this signal instead of creating its own AbortController.
     */
    signal?: AbortSignal;

    /**
     * Reports how much of the request body has been sent. When provided, the request is sent with
     * XMLHttpRequest instead of fetch, because fetch cannot report upload progress.
     */
    onUploadProgress?: UploadProgressCallback;
}
interface IOldAPIHandlerProps<T> extends IAPIHandlerPropsBase<T> {
    version?: APIHandlerVersion.none;
}
interface IAPIHandlerProps<T> extends IAPIHandlerPropsBase<T> {
    version: APIHandlerVersion.cancellable;
}

/**
 * Recursively builds url path
 * @param data
 * @param prefix
 * @example
 * convertObjectToURL({ key: 1, subObj: { key2: 2 } })
 * will return "key=1&subObj.key2=2"
 */
export function convertObjectToURL(data: any, prefix: string = ""): string {
    return Object.keys(data)
        .filter((k: string) => {
            const value = (data as Record<string, unknown>)[k];
            return (
                value !== undefined && value !== null && (!Array.isArray(value) || value.length > 0)
            );
        })
        .map((k: string) => {
            const qsKey = `${prefix ? prefix + "." : prefix}${encodeURIComponent(k)}`;
            let dataForKey = data[k];
            if (Array.isArray(dataForKey)) {
                const joinedData = dataForKey.join(`&${qsKey}=`);
                return `${qsKey}=${joinedData}`;
            } else if (typeof dataForKey === "object") {
                if (moment.isMoment(dataForKey)) {
                    // send moment dates up as local date/time in BT (ISO without offset)
                    dataForKey = dataForKey.format("YYYY-MM-DDTHH:mm:ss");
                } else if (dataForKey instanceof Date) {
                    // send dates up as local date/time in BT (ISO without offset)
                    dataForKey = format(dataForKey, "yyyy-MM-dd'T'HH:mm:ss");
                } else {
                    return convertObjectToURL(dataForKey, `${prefix ? prefix + "." + k : k}`);
                }
            }

            return `${qsKey}=${encodeURIComponent(dataForKey)}`;
        })
        .join("&");
}

/**
 * Returns a replacer function
 * @param sendUndefinedAsNull should undefined values be converted to null? This is used for legacy api calls that require null
 */
export function convertObjectToJSON(
    sendUndefinedAsNull: boolean = false,
): (this: any, key: string, value: any) => any {
    return (key: string, value: any) => {
        if (sendUndefinedAsNull && value === undefined) {
            return null;
        }

        if (typeof value === "object") {
            for (const k in value) {
                const objValue = value[k];
                if (moment.isMoment(objValue)) {
                    // send moment dates up as local date/time in BT (ISO without offset)
                    value[k] = objValue.format("YYYY-MM-DDTHH:mm:ss");
                } else if (objValue instanceof Date) {
                    // send dates up as local date/time in BT (ISO without offset)
                    value[k] = format(objValue, "yyyy-MM-dd'T'HH:mm:ss");
                } else if (objValue instanceof BTSelectItem) {
                    value[k] = {
                        id: objValue.id,
                        name: objValue.title,
                        selected: objValue.selected,
                        extraData: objValue.extraData,
                    };
                }
            }
        }

        return value;
    };
}

/**
 *
 * @param url
 * @param props
 * @example await APIHandler(`/api/someEntity/${id}`, { method: "POST", data: newItem, responseType: ClassForResponseType });
 */
export function APIHandler(
    url: string,
    props: IOldAPIHandlerProps<IBlobResponse>,
): Promise<IBlobResponse>;
export function APIHandler<T extends object>(
    url: string,
    props: IOldAPIHandlerProps<T>,
): Promise<T>;

export function APIHandler(
    url: string,
    props: IAPIHandlerProps<IBlobResponse>,
): IAPIHandlerResult<IBlobResponse>;
export function APIHandler<T extends object>(
    url: string,
    props: IAPIHandlerProps<T>,
): IAPIHandlerResult<T>;

export function APIHandler<T extends object>(
    url: string,
    props: IAPIHandlerProps<T> | IOldAPIHandlerProps<T>,
): Promise<T | IBlobResponse> | IAPIHandlerResult<T> {
    const throwExceptionOnAbort =
        props.throwExceptionOnAbort !== undefined ? props.throwExceptionOnAbort : true;

    let urlQS = "";
    // In these scenarios we don't want to add the application/json header as content type won't match
    // currently btFileSystem is used when we are uploading
    const requestOptions = getRequestOptions(
        props.method,
        !props.isMultiPartPostData,
        props.headers,
    );

    const data = props.data || {};

    switch (props.method) {
        case "GET":
            // add data to querystring (if we drop ie11 support or add more polyfills this can be cleaned up)
            const dataCopy = cloneDeep<T>(data);
            const qsParams = convertObjectToURL(dataCopy);

            // Some endpoints are passing in query strings incorrectly
            urlQS = addQsParam(qsParams, url);
            break;

        case "PUT":
        case "POST":
        case "DELETE":
        case "PATCH":
            // add data to the request body
            if (!isNullOrUndefined(props.data)) {
                if (FormData.prototype.isPrototypeOf(props.data)) {
                    requestOptions.body = props.data;

                    // When submitting form data append org links as a query param
                    const qsParams = convertObjectToURL({});
                    urlQS = addQsParam(qsParams, url);
                } else {
                    const dataCopy = cloneDeep<T>(props.data);
                    requestOptions.body = JSON.stringify(
                        dataCopy,
                        convertObjectToJSON(props.sendUndefinedAsNull),
                    );
                }
            }

            break;

        default:
            throw `APIHandler does not support the method type "${props.method}"`;
    }

    const controller = props.signal ? undefined : new AbortController();
    const signal = props.signal ?? controller!.signal;
    const request = props.onUploadProgress
        ? xhrFetch(url + urlQS, { ...requestOptions, signal }, props.onUploadProgress)
        : // credentials: include is needed for edge
          fetch(url + urlQS, { ...requestOptions, signal });
    const response = request
        .catch((e) => {
            throw new NetworkError(e, "NetworkError");
        }) // network error, Create a new exception type and throw it. Then in sentry settings ignore the new exception type
        .then((response) => checkResponseForStatusErrors(response, url, props.isBTAdmin || false))
        .then((response) =>
            processResponse<T>(
                response,
                props.isBTAdmin ?? false,
                url,
                props.responseType as ResponseType<T>,
            ),
        );
    // any errors thrown from .then()'s above are not caught by design

    if (props.version === APIHandlerVersion.cancellable) {
        return {
            response: response.catch((e) => {
                if (isCanceledAPIRequestError(e) || (!throwExceptionOnAbort && signal.aborted)) {
                    return undefined;
                }
                throw e;
            }),
            unhandledResponse: response,
            cancel: () => {
                controller?.abort();
            },
        } as IAPIHandlerResult<T>;
    } else {
        return response;
    }
}

export function addQsParam(qsParams: string, url: string) {
    let urlQS = "";
    if (qsParams.length !== 0) {
        if (url.includes("?")) {
            urlQS += "&" + qsParams;
        } else {
            urlQS += "?" + qsParams;
        }
    }
    return urlQS;
}

/**
 * Returns true if the given URL is an APIX URL whose response is the bare payload rather than a
 * BT_ServiceResponse envelope, so the caller gets the response as-is instead of `response.data`.
 *
 * `/apix/v1/*` is the exception: those are legacy controllers reached through the apix prefix, and
 * they still return the v1 envelope. Treating them as apix would hand the envelope to the entity
 * constructor, which then reads its fields off the wrapper and gets undefined (ado-286427).
 */
function isApiX(pathName: string) {
    return pathName.startsWith("/apix/") && !pathName.startsWith("/apix/v1/");
}
